import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Search, Menu, X, ChevronDown } from 'lucide-react';
import { NotificationBell } from './NotificationManager';
import AuthModal from './AuthModal';

const CATEGORIES = ['Food & Drink', 'Nightlife', 'Fitness & Wellness', 'Activities', 'Free Deals', 'Convenience Stores', 'Liquor Stores'];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/explore?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setMobileOpen(false);
    }
  };

  return (
    <>
      <nav
        data-testid="navbar"
        className="sticky top-0 z-50 backdrop-blur-xl border-b border-[#8A9A8B]/20"
        style={{ backgroundColor: 'rgba(246, 241, 233, 0.92)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
              <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display, serif', color: '#2B2B2B' }}>
                Perkly
              </span>
              <span className="w-2 h-2 rounded-full bg-[#C8A97E]" />
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-6">
              <Link to="/explore" data-testid="nav-explore" className="text-sm font-semibold text-[#2B2B2B]/70 hover:text-[#C8A97E] transition-colors">
                Explore
              </Link>
              {/* Categories dropdown */}
              <div className="relative group">
                <button className="flex items-center gap-1 text-sm font-semibold text-[#2B2B2B]/70 hover:text-[#C8A97E] transition-colors" data-testid="nav-categories">
                  Categories <ChevronDown size={14} />
                </button>
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-52 bg-white rounded-2xl shadow-lg border border-[#8A9A8B]/15 py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  {CATEGORIES.map((cat) => (
                    <Link key={cat} to={`/explore?category=${encodeURIComponent(cat)}`}
                      className="block px-4 py-2 text-sm text-[#2B2B2B] hover:bg-[#F6F1E9] hover:text-[#C8A97E] transition-colors">
                      {cat}
                    </Link>
                  ))}
                </div>
              </div>
              <Link to="/business/register" data-testid="nav-for-businesses" className="text-sm font-semibold text-[#2B2B2B]/70 hover:text-[#C8A97E] transition-colors">
                For Businesses
              </Link>
              <Link to="/pricing" data-testid="nav-pricing" className="text-sm font-semibold text-[#2B2B2B]/70 hover:text-[#C8A97E] transition-colors">
                Pricing
              </Link>
            </div>

            {/* Desktop Search + Auth */}
            <div className="hidden md:flex items-center gap-3">
              <form onSubmit={handleSearch} className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A9A8B]" />
                <input
                  data-testid="nav-search-input"
                  type="text" placeholder="Search deals..."
                  value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-2 text-sm rounded-full border border-[#8A9A8B]/30 bg-white/80 focus:outline-none focus:border-[#C8A97E] focus:ring-2 focus:ring-[#C8A97E]/20 w-48 transition-all"
                />
              </form>

              <NotificationBell />

              {user ? (
                <div className="relative">
                  <button
                    data-testid="user-menu-button"
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border border-[#8A9A8B]/30 hover:border-[#C8A97E] transition-colors"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-[#C8A97E] flex items-center justify-center text-white text-xs font-bold">
                        {user.name?.[0]?.toUpperCase()}
                      </div>
                    )}
                    <span className="text-sm font-semibold text-[#2B2B2B]">{user.name?.split(' ')[0]}</span>
                    <ChevronDown size={12} className="text-[#8A9A8B]" />
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-2xl shadow-lg border border-[#8A9A8B]/15 py-2 z-50">
                      {user.role === 'admin' && (
                        <Link to="/admin" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2 text-sm text-[#2B2B2B] hover:bg-[#F6F1E9]" data-testid="menu-admin">Admin Dashboard</Link>
                      )}
                      {(user.role === 'business' || user.role === 'admin') && (
                        <Link to="/business/dashboard" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2 text-sm text-[#2B2B2B] hover:bg-[#F6F1E9]" data-testid="menu-business-dashboard">Business Dashboard</Link>
                      )}
                      {user.role === 'user' && (
                        <Link to="/student/dashboard" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2 text-sm text-[#2B2B2B] hover:bg-[#F6F1E9]" data-testid="menu-student-dashboard">My Dashboard</Link>
                      )}
                      <Link to="/business/register" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2 text-sm text-[#2B2B2B] hover:bg-[#F6F1E9]">
                        {user.role === 'business' ? 'My Business' : 'Register Business'}
                      </Link>
                      <hr className="my-1 border-[#8A9A8B]/15" />
                      <button onClick={logout} className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50" data-testid="logout-button">
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  data-testid="nav-login-button"
                  onClick={() => setShowAuthModal(true)}
                  className="btn-primary text-sm px-5 py-2"
                >
                  Sign In
                </button>
              )}
            </div>

            {/* Mobile: quick auth — visible in header next to hamburger */}
            <div className="md:hidden flex items-center gap-1.5">
              <NotificationBell />
              {user ? (
                <button
                  onClick={() => setMobileOpen(!mobileOpen)}
                  className="w-8 h-8 rounded-full bg-[#C8A97E] flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                >
                  {user.picture
                    ? <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                    : user.name?.[0]?.toUpperCase()}
                </button>
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="text-xs font-bold text-[#2B2B2B] px-3 py-1.5 rounded-full border border-[#2B2B2B]/25 active:bg-[#2B2B2B]/8"
                  data-testid="mobile-signin-btn"
                >
                  Sign In
                </button>
              )}
              <button data-testid="mobile-menu-toggle" className="p-2 rounded-lg text-[#2B2B2B]" onClick={() => setMobileOpen(!mobileOpen)}>
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileOpen && (
            <div className="md:hidden py-4 border-t border-[#8A9A8B]/20 space-y-3">
              <form onSubmit={handleSearch} className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A9A8B]" />
                <input type="text" placeholder="Search deals, food, events..." value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border border-[#8A9A8B]/30 bg-white focus:outline-none focus:border-[#C8A97E]" />
              </form>
              <Link to="/explore" onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-semibold text-[#2B2B2B]">Explore Deals</Link>
              <Link to="/business/register" onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-semibold text-[#2B2B2B]">For Businesses</Link>
              {user ? (
                <>
                  {user.role === 'admin' && <Link to="/admin" onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-semibold text-[#C8A97E]">Admin Dashboard</Link>}
                  {(user.role === 'business' || user.role === 'admin') && <Link to="/business/dashboard" onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-semibold text-[#C8A97E]">Business Dashboard</Link>}
                  {user.role === 'user' && <Link to="/student/dashboard" onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-semibold text-[#C8A97E]">My Dashboard</Link>}
                  <button onClick={logout} className="w-full text-left py-2 text-sm font-semibold text-red-500">Sign Out</button>
                </>
              ) : (
                <button onClick={() => { setMobileOpen(false); setShowAuthModal(true); }} className="w-full btn-primary text-sm py-2.5 text-center justify-center">
                  Sign In / Register
                </button>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal onClose={() => setShowAuthModal(false)} />
      )}
    </>
  );
}
